import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  ClipboardCheck,
  type LucideIcon,
} from 'lucide-react';
import {
  Avatar,
  Button,
  EmptyState,
  IconTile,
  Inline,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@relay-towing/ui';
import {
  useDriverChecklists,
  useMyInspections,
} from '@/hooks/useTruckInspections';
import { useHybridAuth } from '@/hooks/useHybridAuth';
import type { ChecklistRow, MyInspection } from '@/api/truckInspections';
import { coversIcon, isToday, shortWhen, whenLabel } from '@/lib/inspections';
import { RowListSkeleton } from './components/InspectionSkeletons';
import { AppBottomNav } from '@/components/navigation/AppBottomNav';

type Tab = 'checklists' | 'completed';

/** The rounded card every row list sits in. */
const CARD =
  'divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card';

/**
 * The count beside a segment's label. Primary while its segment is on, which
 * is why it reads the trigger's state rather than being told which tab is
 * showing.
 */
const COUNT_PILL =
  'ml-1.5 inline-flex min-w-5 items-center justify-center rounded-pill ' +
  'bg-border px-1.5 text-muted-foreground ' +
  'group-data-[state=active]:bg-primary ' +
  'group-data-[state=active]:text-primary-foreground';

/**
 * The glyph colour for each tile tone.
 *
 * `IconTile`'s tone paints the tile only, and in dark mode `text-destructive`
 * on `bg-destructive-subtle` is dark red on dark red. The `-subtle-foreground`
 * pair is the one built to sit on it.
 */
const TILE_TEXT = {
  neutral: 'text-muted-foreground',
  warning: 'text-warning-subtle-foreground',
  destructive: 'text-destructive-subtle-foreground',
  success: 'text-success-subtle-foreground',
} as const;

type Tone = keyof typeof TILE_TEXT;

/**
 * One tappable row: a tile, a name, a line under it, a stamp, a chevron.
 *
 * Both lists are the same shape, so they share one. Defined here rather than
 * in the library because nothing else in the app has wanted it yet.
 */
function Row({
  icon,
  tone,
  title,
  subtitle,
  meta,
  metaTone,
  label,
  onClick,
}: {
  icon: LucideIcon;
  tone: Tone;
  title: string;
  subtitle: string;
  meta?: string;
  metaTone?: 'muted' | 'warning';
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="inline"
      aria-label={label}
      onClick={onClick}
      className="w-full items-center justify-start gap-3 rounded-none px-4 py-3 text-left"
    >
      <IconTile icon={icon} size={9} tone={tone} className={TILE_TEXT[tone]} />

      <Stack gap={0} className="min-w-0 flex-1">
        <Inline gap={2} align="baseline" justify="between">
          <Text as="span" variant="body-strong" truncate className="min-w-0">
            {title}
          </Text>
          {meta ? (
            <Text
              as="span"
              variant="caption-strong"
              tone={metaTone}
              className="shrink-0"
            >
              {meta}
            </Text>
          ) : null}
        </Inline>
        <Text as="span" variant="body-sm" tone="muted" truncate>
          {subtitle}
        </Text>
      </Stack>

      <ChevronRight className="shrink-0 text-muted-foreground" />
    </Button>
  );
}

/**
 * The list the driver lands on: the checklists this yard runs, and the reports
 * this driver has already filed.
 *
 * Picking the checklist comes first. What it is being run on, a truck or a
 * piece of equipment, is named once the run starts.
 */
export default function InspectionsScreen() {
  const navigate = useNavigate();
  const { user } = useHybridAuth();
  const [tab, setTab] = useState<Tab>('checklists');

  const checklists = useDriverChecklists(tab === 'checklists');
  const history = useMyInspections(tab === 'completed');

  const rows = checklists.data ?? [];
  const done = history.data ?? [];

  const driverName = typeof user?.name === 'string' ? user.name.trim() : '';

  /**
   * One row, one thing to do: start this checklist.
   *
   * The line under the name says how long it is and when this driver last ran
   * it, on what. A checklist nobody ran today is not a fault: a yard can keep
   * three of them and only one applies to a given shift.
   */
  const renderChecklist = (row: ChecklistRow) => {
    const questions = `${row.itemCount} question${
      row.itemCount === 1 ? '' : 's'
    }`;
    const last = row.lastRun
      ? ` · You ran this ${whenLabel(row.lastRun.submittedAt)} on ${
          row.lastRun.subjectLabel
        }`
      : ' · Not run yet';

    return (
      <Row
        key={row.id}
        icon={coversIcon(row.covers)}
        tone="neutral"
        title={row.name}
        subtitle={`${questions}${last}`}
        meta={isToday(row.lastRun?.submittedAt) ? 'Ran today' : undefined}
        metaTone="muted"
        label={`Start ${row.name}`}
        onClick={() => navigate(`/inspections/run/${row.id}`)}
      />
    );
  };

  const renderHistoryRow = (row: MyInspection) => {
    const issues = row.status === 'ISSUES_FOUND';
    const flagged = row._count.defects;
    const result =
      flagged > 0
        ? `${flagged} issue${flagged === 1 ? '' : 's'} flagged`
        : 'No issues';
    const name = row.subjectLabel;

    return (
      <Row
        key={row.id}
        icon={issues ? AlertTriangle : Check}
        tone={issues ? 'destructive' : 'success'}
        title={name}
        subtitle={`${row.templateName} · ${result}`}
        meta={shortWhen(row.submittedAt)}
        metaTone="muted"
        label={`Open report, ${name}`}
        onClick={() => navigate(`/inspections/report/${row.id}`)}
      />
    );
  };

  /*
   * `isPending`, not `isLoading`. A query that has not been switched on yet
   * reports `isLoading: false`, and the tab that is not showing is switched
   * off, so the first tap on Completed drew "No inspections yet" for a frame
   * before the skeleton and the real list. The tab being looked at is always
   * the one that is switched on, so `isPending` is exactly "nothing to draw".
   */
  const loading =
    tab === 'completed' ? history.isPending : checklists.isPending;
  // A failed load is not an empty list. Showing "No checklists yet" when the
  // request 400d is how a broken company header stayed invisible.
  const failed = tab === 'completed' ? history.isError : checklists.isError;
  const retry = tab === 'completed' ? history.refetch : checklists.refetch;
  const isEmpty = tab === 'completed' ? done.length === 0 : rows.length === 0;

  /*
   * A count for a tab that has never been opened would mean fetching both
   * lists on arrival. The pill waits for the tab instead: the one you are on
   * is always counted, and the other fills in the first time you look at it.
   */
  const countFor = (which: Tab) =>
    which === 'completed'
      ? history.data && done.length > 0
        ? done.length
        : null
      : checklists.data && rows.length > 0
        ? rows.length
        : null;

  const panel = loading ? (
    <RowListSkeleton
      label={
        tab === 'completed' ? 'Loading your inspections' : 'Loading checklists'
      }
    />
  ) : failed ? (
    <EmptyState
      icon={AlertTriangle}
      title="Could not load this"
      description="Check your signal and try again."
      action={
        <Button variant="outline" onClick={() => retry()}>
          Try again
        </Button>
      }
    />
  ) : isEmpty ? (
    <EmptyState
      icon={ClipboardCheck}
      title={
        tab === 'completed'
          ? 'No completed inspections yet.'
          : 'No checklists yet'
      }
      description={
        tab === 'completed' ? undefined : 'Ask the office to add one.'
      }
      className="rounded-2xl border border-dashed border-border"
    />
  ) : tab === 'completed' ? (
    <div className={CARD}>{done.map(renderHistoryRow)}</div>
  ) : (
    <div className={CARD}>{rows.map(renderChecklist)}</div>
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as Tab)}
        appearance="pill"
      >
        <header className="border-b border-border bg-background px-4 pb-3 pt-4">
          <Inline gap={3} align="center" justify="between">
            <Stack gap={0}>
              <Text
                as="span"
                variant="body-sm-strong"
                className="tracking-tight text-primary"
              >
                RELAY TOW
              </Text>
              <Text as="h1" variant="h3">
                Inspections
              </Text>
            </Stack>
            {driverName ? <Avatar name={driverName} tone="primary" /> : null}
          </Inline>

          <TabsList fullWidth className="mt-3">
            <TabsTrigger
              value="checklists"
              fullWidth
              className="group select-none"
            >
              Checklists
              {countFor('checklists') !== null && (
                <Text
                  as="span"
                  variant="caption-strong"
                  tone="inherit"
                  numeric
                  className={COUNT_PILL}
                >
                  {countFor('checklists')}
                </Text>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              fullWidth
              className="group select-none"
            >
              Completed
              {countFor('completed') !== null && (
                <Text
                  as="span"
                  variant="caption-strong"
                  tone="inherit"
                  numeric
                  className={COUNT_PILL}
                >
                  {countFor('completed')}
                </Text>
              )}
            </TabsTrigger>
          </TabsList>
        </header>

        <TabsContent value="checklists" className="p-4">
          {panel}
        </TabsContent>
        <TabsContent value="completed" className="p-4">
          {panel}
        </TabsContent>
      </Tabs>

      <AppBottomNav activeTab="inspections" />
    </div>
  );
}
